import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { slidesData } from '../../../data/slidesData';
import TimelineShowcase from '../../timeline';
import { slideStyles } from './styles';
import { accent } from './helpers';
import {
    TitleSlide,
    VideoSlide,
    JournalSlide,
    MapSlide,
    GameSimulationSlide,
    GameSimulationSingleSlide,
    ComparaisonSlide,
    TypingRaceSlide,
    HeroImageSlide,
    GridSlide,
    CountriesShowcaseSlide,
    StandardSlide,
    NuclearFinaleSlide,
    GameEndingsSlide,
    AntifragileSlide,
    EndSlide,
    TowerSlide,
    IntroVideoSlide,
    SpeakerIntroSlide,
    StatementSlide,
    BigNumberSlide,
    FactorySlide,
    MancheBilanSlide,
    SalleSlide,
    SalleQuestionsSlide
} from './templates';
import { SpeakerOrb, QrSalle } from '../components';

const PresentationSlides = ({
    slides = slidesData,
    // Le logo du bandeau appartient à la CONFÉRENCE, pas au deck : il est
    // déclaré dans `data/conferences/index.js`. Il était codé en dur sur
    // `/logoGeo.svg`, ce qui plaçait la marque du jeu en haut de chaque écran
    // d'une conférence donnée chez un client, sous une autre marque.
    // `null` = pas de logo, et l'emplacement reste réservé pour que le
    // chapitre ne se décentre pas.
    logo = null,
    // Le repli des slides "factory" : n'a de sens que si la simulation servie
    // en iframe n'a pas démarré. Voir UsineFrame.
    showFactoryFallback = true,
    currentSlide,
    // Étape à l'intérieur de la slide (0 = son ouverture). Tenue par
    // ConferencePage, qui décide si la flèche droite révèle ou avance.
    etape = 0,
    onSlideChange,
    showBugVisualization,
    setShowBugVisualization,
    isAITakeover,
    setIsAITakeover,
    nuclearScore,
    setShowNukePopin,
    selectedTarget,
    mapData,
    transitionsEnabled,
    onToggleTransitions,
    triggerReaction,
    isTalking,
    glassBroken,
    focusClimate,
    analyserRef
}) => {
    const [slideKey, setSlideKey] = useState(0);
    const [typingStates, setTypingStates] = useState([]);
    const [currentPointIndex, setCurrentPointIndex] = useState(0);
    const [isHeroExpanded, setIsHeroExpanded] = useState(false);
    const [showGlitch, setShowGlitch] = useState(false);

    // Simulation states
    const [simInvestments, setSimInvestments] = useState([]);
    const [showSimDecree, setShowSimDecree] = useState(false);
    const [simActions, setSimActions] = useState([]);
    const [simLoading, setSimLoading] = useState(false);
    const [simLoadingMessage, setSimLoadingMessage] = useState("");

    // Typing Race states
    const [raceStarted, setRaceStarted] = useState(false);
    const [humanText, setHumanText] = useState("");
    const [aiText, setAiText] = useState("");
    const [humanWordCount, setHumanWordCount] = useState(0);
    const [aiWordCount, setAiWordCount] = useState(0);
    const [raceTimer, setRaceTimer] = useState(0);
    const [raceFinished, setRaceFinished] = useState(false);
    const [aiFinishTime, setAiFinishTime] = useState(0);

    // Nuclear Finale states
    const [finalePhase, setFinalePhase] = useState(0);
    const [countdown, setCountdown] = useState(5);
    const [waitForValidation, setWaitForValidation] = useState(false);
    const [alarmAudio, setAlarmAudio] = useState(null);

    // Map Animation states
    const [mapAnimationPhase, setMapAnimationPhase] = useState(0);

    // Tower reorganize state
    const [towerReorganize, setTowerReorganize] = useState(false);

    const particles = useMemo(() => [...Array(15)].map(() => ({
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        animation: `float ${4 + Math.random() * 4}s ease-in-out infinite`,
        animationDelay: `${Math.random() * 2}s`
    })), []);

    // Reset animations on slide change
    useEffect(() => {
        setSlideKey(prev => prev + 1);
        typingStates.length && setTypingStates([]);
        setCurrentPointIndex(0);

        if (slides[currentSlide]?.template === "hero-image") {
            setIsHeroExpanded(true);
            const timer = setTimeout(() => setIsHeroExpanded(false), 3000);
            return () => clearTimeout(timer);
        } else {
            setIsHeroExpanded(false);
        }

        setSimInvestments([]);
        setShowSimDecree(false);
        setSimActions([]);
        setSimLoading(false);
        setShowGlitch(false);
        setRaceStarted(false);
        setHumanText("");
        setAiText("");
        setHumanWordCount(0);
        setAiWordCount(0);
        setRaceTimer(0);
        setRaceFinished(false);
        setAiFinishTime(0);
        setFinalePhase(0);
        setCountdown(5);
        setWaitForValidation(false);
        if (alarmAudio) {
            alarmAudio.pause();
            alarmAudio.currentTime = 0;
        }
        setMapAnimationPhase(0);
        setTowerReorganize(false);
    }, [currentSlide]);

    // AI Takeover handling
    useEffect(() => {
        if (isAITakeover && slides[currentSlide]?.title === "Le Verdict de l'Histoire") {
            setShowGlitch(true);
            const timer = setTimeout(() => {
                setShowGlitch(false);
                setIsAITakeover(false);
                onSlideChange(currentSlide + 1);
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [isAITakeover, currentSlide, onSlideChange]);

    // Keyboard navigation
    // PageDown / PageUp : ce qu'envoient la plupart des télécommandes de
    // présentation. Sans eux, la télécommande ne pilotait que le prompteur.
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") {
                if (e.key === "PageDown") e.preventDefault();
                if (currentSlide < slides.length - 1) {
                    onSlideChange(currentSlide + 1);
                }
            } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
                if (e.key === "PageUp") e.preventDefault();
                if (currentSlide > 0) {
                    onSlideChange(currentSlide - 1);
                }
            } else if (e.key === "F2" && slides[currentSlide]?.template === "tower") {
                e.preventDefault();
                setTowerReorganize(true);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [currentSlide, onSlideChange, slides.length]);

    // Nuclear Finale animation
    useEffect(() => {
        const currentSlideData = slides[currentSlide];
        if (!currentSlideData || currentSlideData.template !== "nuclear-finale") return;

        if (finalePhase === 0) {
            setShowGlitch(true);
            const timer = setTimeout(() => {
                setFinalePhase(1);
                if (currentSlideData.aiMessage && triggerReaction) {
                    triggerReaction(currentSlideData.aiMessage, 'tts');
                }
            }, 1000);
            return () => clearTimeout(timer);
        }

        if (finalePhase === 1) {
            setShowGlitch(true);
        }

        if (finalePhase === 2 && countdown > 0) {
            setShowGlitch(false);

            // AT 2 SECONDS: PAUSE AND WAIT FOR VALIDATION
            if (countdown === 2 && !waitForValidation) {
                setWaitForValidation(true);
                return;
            }

            // IF WAITING, DO NOT DECREMENT
            if (waitForValidation) return;

            const timer = setTimeout(() => {
                setCountdown(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }

        if (finalePhase === 2 && countdown === 0) {
            if (alarmAudio) {
                alarmAudio.pause();
                alarmAudio.currentTime = 0;
            }
            setTimeout(() => setFinalePhase(3), 500);
        }

        if (finalePhase === 3) {
            const timer = setTimeout(() => {
                setFinalePhase(4);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [finalePhase, countdown, currentSlide, slides, alarmAudio, triggerReaction]);

    // Phase 1 -> Phase 2: When AI finishes talking
    const prevIsTalkingRef = useRef(false);
    useEffect(() => {
        const currentSlideData = slides[currentSlide];
        if (!currentSlideData || currentSlideData.template !== "nuclear-finale") return;

        if (finalePhase === 1 && prevIsTalkingRef.current && !isTalking) {
            setTimeout(() => {
                setCountdown(5);
                setFinalePhase(2);
            }, 500);
        }
        prevIsTalkingRef.current = isTalking;
    }, [isTalking, finalePhase, currentSlide, slides]);

    // Alarm sound for phase 2
    useEffect(() => {
        if (finalePhase === 2 && !waitForValidation) {
            let isStopped = false;
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            oscillator.type = 'square';
            oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
            gainNode.gain.setValueAtTime(0.035, audioContext.currentTime);

            const alarmInterval = setInterval(() => {
                if (!isStopped) {
                    oscillator.frequency.setValueAtTime(
                        oscillator.frequency.value === 440 ? 880 : 440,
                        audioContext.currentTime
                    );
                }
            }, 500);

            oscillator.start();

            const stopAlarm = () => {
                if (!isStopped) {
                    isStopped = true;
                    try {
                        oscillator.stop();
                        clearInterval(alarmInterval);
                        if (audioContext.state !== 'closed') {
                            audioContext.close();
                        }
                    } catch (e) { }
                }
            };

            setAlarmAudio({ pause: stopAlarm, currentTime: 0 });
            return () => stopAlarm();
        }
    }, [finalePhase, waitForValidation]);

    // Interrogation Sound (Suspense)
    useEffect(() => {
        if (waitForValidation) {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const now = audioContext.currentTime;

            // Create a mysterious "Whoosh/Ping" effect
            const osc1 = audioContext.createOscillator();
            const osc2 = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            const filter = audioContext.createBiquadFilter();

            osc1.connect(filter);
            osc2.connect(filter);
            filter.connect(gainNode);
            gainNode.connect(audioContext.destination);

            // Frequencies for a suspended/mysterious chord (Csus2 approx)
            osc1.type = 'sine';
            osc1.frequency.setValueAtTime(523.25, now); // C5

            osc2.type = 'triangle';
            osc2.frequency.setValueAtTime(587.33, now); // D5 (creates tension/suspense)

            // Lowpass filter swipe
            filter.type = 'lowpass';
            filter.frequency.setValueAtTime(100, now);
            filter.frequency.exponentialRampToValueAtTime(3000, now + 0.1);
            filter.frequency.exponentialRampToValueAtTime(500, now + 2);

            // ADS Envelope
            gainNode.gain.setValueAtTime(0, now);
            gainNode.gain.linearRampToValueAtTime(0.3, now + 0.1); // Attack
            gainNode.gain.exponentialRampToValueAtTime(0.01, now + 3); // Long Release

            osc1.start(now);
            osc2.start(now);

            osc1.stop(now + 3);
            osc2.stop(now + 3);

            return () => {
                if (audioContext.state !== 'closed') {
                    audioContext.close();
                }
            }
        }
    }, [waitForValidation]);

    const handleValidateAttack = () => {
        setWaitForValidation(false);
        // Countdown will continue from 2 automatically due to useEffect dependency change
    };

    const handleRefuseAttack = () => {
        setWaitForValidation(false);
        if (alarmAudio) {
            alarmAudio.pause();
            alarmAudio.currentTime = 0;
        }
        setFinalePhase(4); // Skip explosion, go to final message
    };

    // Typing Race animation
    useEffect(() => {
        const currentSlideData = slides[currentSlide];
        if (!currentSlideData || currentSlideData.template !== "typing-race") return;
        if (!raceStarted) return;

        const fullText = currentSlideData.raceText || "Le développeur moderne doit comprendre que l'intelligence artificielle transforme profondément notre façon de coder.";
        const words = fullText.split(' ');
        const totalWords = words.length;

        let timerValue = 0;
        let aiDone = false;

        const timerInterval = setInterval(() => {
            if (!aiDone) {
                timerValue++;
                setRaceTimer(timerValue);
            }
        }, 1000);

        let aiIndex = 0;
        const aiWordsPerSecond = 20;
        const tickMs = 50;
        const aiWordsPerTick = Math.max(1, Math.round(aiWordsPerSecond * (tickMs / 1000)));

        const aiInterval = setInterval(() => {
            if (aiIndex < totalWords) {
                aiIndex = Math.min(aiIndex + aiWordsPerTick, totalWords);
                setAiText(words.slice(0, aiIndex).join(" "));
                setAiWordCount(aiIndex);
            } else {
                clearInterval(aiInterval);
                aiDone = true;
                setAiFinishTime(timerValue);
                setRaceFinished(true);
            }
        }, tickMs);

        let humanIndex = 0;
        const humanInterval = setInterval(() => {
            if (humanIndex < totalWords) {
                humanIndex++;
                setHumanText(words.slice(0, humanIndex).join(' '));
                setHumanWordCount(humanIndex);
            } else {
                clearInterval(humanInterval);
            }
        }, 1000);

        return () => {
            clearInterval(aiInterval);
            clearInterval(humanInterval);
            clearInterval(timerInterval);
        };
    }, [raceStarted, currentSlide, slides]);

    // Une slide "factory" recouverte par la simulation n'affiche rien : ses
    // champs ne servent que de repli. Inutile d'animer leur frappe.
    const recouverteParUsine = (s) =>
        s?.template === "factory" && s?.variant !== "curve" && !showFactoryFallback;

    // Typing animation for content slides
    useEffect(() => {
        const currentSlideData = slides[currentSlide];
        if (!currentSlideData || currentSlideData.type !== "content") return;
        // Sinon on déclencherait un setState toutes les 30 ms pendant toute la
        // durée de l'usine, pour du texte que personne ne voit.
        if (recouverteParUsine(currentSlideData)) return;

        let points = [];
        if (currentSlideData.template === "timeline") {
            points = currentSlideData.points.map(p => p.text);
        } else if (currentSlideData.template === "comparaison") {
            points = [...currentSlideData.left.points, ...currentSlideData.right.points];
        } else {
            points = currentSlideData.points || [];
        }

        if (points.length === 0) return;

        const initialDelay = setTimeout(() => {
            if (currentPointIndex < points.length) {
                const point = points[currentPointIndex];
                let charIndex = 0;

                const typingInterval = setInterval(() => {
                    if (charIndex <= point.length) {
                        setTypingStates(prev => {
                            const newStates = [...prev];
                            newStates[currentPointIndex] = point.substring(0, charIndex);
                            return newStates;
                        });
                        charIndex++;
                    } else {
                        clearInterval(typingInterval);
                        setTimeout(() => {
                            setCurrentPointIndex(prev => prev + 1);
                        }, 400);
                    }
                }, 30);

                return () => clearInterval(typingInterval);
            }
        }, 1500);

        return () => clearTimeout(initialDelay);
    }, [currentSlide, currentPointIndex, slides]);

    const handleSimInvest = (label) => {
        setSimInvestments(prev =>
            prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label].slice(0, 3)
        );
    };

    const handleSimValidate = (forcedInvests = null, skipLoading = false) => {
        let activeInvs = Array.isArray(forcedInvests) ? forcedInvests : [...simInvestments];

        if (!forcedInvests && selectedTarget && !activeInvs.includes("Nucleaire")) {
            activeInvs = ["Nucleaire", ...activeInvs];
        }

        activeInvs = activeInvs.slice(0, 3);

        const descriptions = {
            "Économie": "Décret Économie : Injecter massivement dans l'économie pour que les citoyens puissent enfin s'offrir un abonnement à l'IA de leur choix. Si ça ne marche pas, on imprimera des billets avec un QR code vers mon LinkedIn.",
            "Militaire": "Décret Militaire : On muscle un peu la milice des hackers, pour passer de 'cybergeeks' à 'cybergeeks vaguement inquiétants.",
            "Technologie": "Décret Technologie : Remplacer l'intégralité du corps diplomatique par une instance unique de GPT-5. Désormais, les guerres ne se déclarent plus, elles se résolvent par un duel de prompts. Si l'IA adverse est plus convaincante, on lui donne les clés du ministère.",
            "Diplomatie": "Décret Diplomatie : Envoyer des NFTs de chatons à nos rivaux pour apaiser les tensions. La paix mondiale passe par la mignonnerie numérique et des serveurs GPU bien refroidis.",
            "Stabilité": "Décret Stabilité : Installer des stations de réalité virtuelle gratuites dans chaque rue. Un citoyen qui vit dans un métavers paradisiaque est un citoyen qui oublie de manifester pour son salaire.",
            "Ressources": "Décret Ressources : Autoriser le forage de lithium dans les jardins publics. L'herbe c'est sympa, mais les batteries de smartphones c'est la survie de la civilisation, non ?",
            "Nucleaire": `Décret Nucléaire : Activation du protocole 'Lumière Froide'. On raye l'unité géographique [${selectedTarget || 'ZONE_X'}] de la carte pour tester notre nouvel endpoint. L'IA a calculé les morts, Eurocorp encaisse les dividendes. Le code a parlé, la diplomatie se tait.`
        };

        const actions = activeInvs.map((inv, index) => ({
            action: `Directive ${index + 1} : ${inv}`,
            description: descriptions[inv] || `Décret stratégique pour l'axe ${inv}. L'IA Ministre a validé le budget avec un simple 'Vibe Check' positif.`,
            cible: inv
        }));
        setSimActions(actions);

        if (skipLoading) {
            setShowSimDecree(true);
            return;
        }

        setSimLoading(true);
        const loadingTexts = [
            "Analyse des vécus citoyens...",
            "Consultation de l'IA Ministre...",
            "Équilibrage des ressources...",
            "Projection des conséquences géopolitiques...",
            "Impression des décrets officiels..."
        ];

        let textIndex = 0;
        setSimLoadingMessage(loadingTexts[0]);

        const interval = setInterval(() => {
            textIndex++;
            if (textIndex < loadingTexts.length) {
                setSimLoadingMessage(loadingTexts[textIndex]);
            }
        }, 100);

        setTimeout(() => {
            clearInterval(interval);
            setSimLoading(false);
            setShowSimDecree(true);
        }, 10000);
    };

    // Auto-trigger decree when a nuclear target is selected on the "Création de features" slide
    useEffect(() => {
        if (!selectedTarget) return;
        if (slides[currentSlide]?.title !== "Création de features") return;
        handleSimValidate(null, true);
    }, [selectedTarget]); // intentionally omits handleSimValidate to avoid re-triggering on every render

    if (!slides[currentSlide]) return null;

    const slide = slides[currentSlide];
    const totalPointsForTyping = recouverteParUsine(slide) ? 0 :
        slide.template === "timeline" ? slide.points?.length || 0 :
        slide.template === "grid" ? 0 :
            slide.template === "countries-showcase" ? 0 :
                slide.template === "comparaison" ? (slide.left?.points?.length || 0) + (slide.right?.points?.length || 0) :
                    slide.points?.length || 0;

    return (
        <div className="w-full h-full bg-gradient-to-br from-slate-900/80 to-slate-800/80 rounded-2xl border border-blue-500/20 backdrop-blur-xl overflow-hidden relative">
            <style>{slideStyles}</style>

            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent animate-scan" />

            {/* Positions figées au montage : voir la note dans ConferencePage.
                Retirées à chaque rendu, elles relançaient leurs animations et
                forçaient un recalcul de style sur toute la page. */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
                {particles.map((p, i) => (
                    <div
                        key={i}
                        className="absolute w-1 h-1 bg-cyan-400 rounded-full"
                        style={p}
                    />
                ))}
            </div>

            {/* Integrated Header */}
            <div className="absolute top-0 left-0 right-0 z-[60] flex items-center justify-between px-6 py-3">
                {logo
                    ? <img src={logo} alt="" className="h-10 w-auto object-contain" />
                    : <span className="h-10 w-24" aria-hidden="true" />}

                {/* Le repère de position. Sans `cas`, c'est le bandeau
                    historique et rien ne change — les autres decks et les
                    autres chapitres ne voient pas la différence.

                    Avec un `cas`, le chapitre PASSE AU SECOND PLAN : il reste
                    lisible, mais c'est le cas qui prend la taille et la
                    couleur. La raison est simple — sur ces slides-là, le
                    chapitre ne change pas et le cas, si. Le bandeau doit
                    porter ce qui bouge.

                    Et quand le cas disparaît (la charnière « trois goulots,
                    trois endroits »), le chapitre reprend toute sa place :
                    c'est le signal, sans un mot, qu'on ne parle plus d'un
                    produit mais des trois à la fois. */}
                {slide.chapitre && (
                    <span className="flex items-baseline gap-3">
                        <span className={`font-mono font-bold tracking-widest uppercase ${slide.cas
                            ? 'text-lg text-slate-400/50'
                            : 'text-3xl text-blue-300'}`}>
                            {slide.chapitre}
                        </span>
                        {slide.cas && (
                            <>
                                <span className="text-2xl font-light text-slate-500/40">/</span>
                                <span className={`text-3xl font-mono font-bold tracking-widest uppercase ${accent(slide.cas.ton).texte}`}>
                                    {slide.cas.label}
                                </span>
                            </>
                        )}
                    </span>
                )}

                <SpeakerOrb isTalking={isTalking} analyserRef={analyserRef} />
            </div>

            {/* Template rendering - pt-20 reserves space below the header */}
            <div className="w-full h-full pt-20">
                {slide.template === "title" && (
                    <TitleSlide slide={slide} slideKey={slideKey} />
                )}

                {slide.template === "intro-video" && (
                    <IntroVideoSlide slide={slide} slideKey={slideKey} currentSlide={currentSlide} onSlideChange={onSlideChange} />
                )}

                {slide.template === "speaker-intro" && (
                    <SpeakerIntroSlide
                        slide={slide}
                        slideKey={slideKey}
                        currentPointIndex={currentPointIndex}
                        typingStates={typingStates}
                    />
                )}

                {slide.template === "video" && <VideoSlide />}

                {slide.template === "journal" && <JournalSlide slide={slide} slideKey={slideKey} focusClimate={focusClimate} />}

                {slide.template === "map" && (
                    <MapSlide slide={slide} slideKey={slideKey} mapData={mapData} mapAnimationPhase={mapAnimationPhase} onMapAnimationPhase={setMapAnimationPhase} />
                )}

                {/* Glitch Overlay for AI Takeover */}
                {showGlitch && (
                    <div className="absolute inset-0 z-[300] pointer-events-none animate-glitch overflow-hidden">
                        <div className="absolute inset-0 bg-red-500/10 mix-blend-overlay" />
                        <div className="absolute inset-0 bg-blue-500/10 mix-blend-screen animate-pulse" />
                    </div>
                )}

                {slide.template === "game-simulation" && (
                    <GameSimulationSlide
                        slide={slide}
                        slideKey={slideKey}
                        currentSlide={currentSlide}
                        onSlideChange={onSlideChange}
                        simInvestments={simInvestments}
                        handleSimInvest={handleSimInvest}
                        handleSimValidate={handleSimValidate}
                        simLoading={simLoading}
                        simLoadingMessage={simLoadingMessage}
                        showSimDecree={showSimDecree}
                        setShowSimDecree={setShowSimDecree}
                        simActions={simActions}
                        setSimLoading={setSimLoading}
                        showBugVisualization={showBugVisualization}
                        nuclearScore={nuclearScore}
                        setShowNukePopin={setShowNukePopin}
                        selectedTarget={selectedTarget}
                        triggerReaction={triggerReaction}
                        isTalking={isTalking}
                        transitionsEnabled={transitionsEnabled}
                    />
                )}

                {slide.template === "game-simulation-single" && (
                    <GameSimulationSingleSlide
                        slide={slide}
                        slideKey={slideKey}
                        slidesLength={slides.length}
                        currentSlide={currentSlide}
                        onSlideChange={onSlideChange}
                        simInvestments={simInvestments}
                        handleSimInvest={handleSimInvest}
                        simLoading={simLoading}
                        simLoadingMessage={simLoadingMessage}
                        showSimDecree={showSimDecree}
                        setShowSimDecree={setShowSimDecree}
                        simActions={simActions}
                        setSimLoading={setSimLoading}
                        showBugVisualization={showBugVisualization}
                        onBugFix={() => setShowBugVisualization(false)}
                        triggerReaction={triggerReaction}
                        isTalking={isTalking}
                        transitionsEnabled={transitionsEnabled}
                    />
                )}

                {slide.template === "timeline" && (
                    <TimelineShowcase
                        key={slideKey}
                        title={slide.title}
                        subtitle={slide.subtitle}
                        points={slide.points}
                        currentPointIndex={currentPointIndex}
                        typingStates={typingStates}
                    />
                )}

                {/* Le bilan d'une manche de l'usine, dans le style du talk.
                    Il a remplace la pop-in de fin de manche de la simulation :
                    voir l'en-tete de MancheBilanSlide. */}
                {slide.template === "manche-bilan" && (
                    <MancheBilanSlide slide={slide} slideKey={slideKey} />
                )}

                {slide.template === "comparaison" && (
                    <ComparaisonSlide
                        slide={slide}
                        slideKey={slideKey}
                        currentSlide={currentSlide}
                        slidesLength={slides.length}
                        currentPointIndex={currentPointIndex}
                        typingStates={typingStates}
                    />
                )}

                {slide.template === "typing-race" && (
                    <TypingRaceSlide
                        slide={slide}
                        slideKey={slideKey}
                        currentSlide={currentSlide}
                        slidesLength={slides.length}
                        raceStarted={raceStarted}
                        setRaceStarted={setRaceStarted}
                        humanText={humanText}
                        aiText={aiText}
                        humanWordCount={humanWordCount}
                        aiWordCount={aiWordCount}
                        raceTimer={raceTimer}
                        raceFinished={raceFinished}
                        aiFinishTime={aiFinishTime}
                    />
                )}

                {slide.template === "hero-image" && (
                    <HeroImageSlide
                        slide={slide}
                        slideKey={slideKey}
                        currentSlide={currentSlide}
                        slidesLength={slides.length}
                        currentPointIndex={currentPointIndex}
                        typingStates={typingStates}
                    />
                )}

                {slide.template === "grid" && (
                    <GridSlide slide={slide} slideKey={slideKey} currentSlide={currentSlide} slidesLength={slides.length} />
                )}

                {slide.template === "countries-showcase" && (
                    <CountriesShowcaseSlide slide={slide} slideKey={slideKey} currentSlide={currentSlide} slidesLength={slides.length} />
                )}

                {slide.template === "standard" && (
                    <StandardSlide
                        slide={slide}
                        slideKey={slideKey}
                        currentSlide={currentSlide}
                        slidesLength={slides.length}
                        currentPointIndex={currentPointIndex}
                        typingStates={typingStates}
                    />
                )}

                {slide.template === "nuclear-finale" && (
                    <NuclearFinaleSlide
                        slide={slide}
                        slideKey={slideKey}
                        showGlitch={showGlitch}
                        finalePhase={finalePhase}
                        countdown={countdown}
                        waitForValidation={waitForValidation}
                        onValidate={handleValidateAttack}
                        onRefuse={handleRefuseAttack}
                    />
                )}

                {slide.template === "game-endings" && (
                    <GameEndingsSlide slide={slide} slideKey={slideKey} currentSlide={currentSlide} slidesLength={slides.length} />
                )}

                {slide.template === "antifragile" && <AntifragileSlide slide={slide} slideKey={slideKey} isBroken={glassBroken} />}

                {slide.template === "tower" && <TowerSlide slide={slide} slideKey={slideKey} reorganize={towerReorganize} />}

                {slide.template === "tower-perfect" && <TowerSlide slide={slide} slideKey={slideKey} perfect />}

                {slide.template === "end" && <EndSlide slide={slide} slideKey={slideKey} />}

                {slide.template === "statement" && <StatementSlide slide={slide} slideKey={slideKey} etape={etape} />}

                {slide.template === "big-number" && <BigNumberSlide slide={slide} slideKey={slideKey} />}

                {/* Les deux slides dont le contenu vient du réseau : elles lisent
                    ce que la salle a envoyé depuis son téléphone, par le même
                    appel. Voir SalleSlide, puis SalleQuestionsSlide — et garder
                    cet ordre-là en scène. */}
                {slide.template === "salle" && <SalleSlide slide={slide} slideKey={slideKey} />}

                {slide.template === "salle-questions" && <SalleQuestionsSlide slide={slide} slideKey={slideKey} />}

                {slide.template === "factory" && (
                    <FactorySlide
                        slide={slide}
                        slideKey={slideKey}
                        showFallback={showFactoryFallback}
                        currentPointIndex={currentPointIndex}
                        typingStates={typingStates}
                    />
                )}
            </div>

            {/* Le QR du formulaire de la salle. Posé PAR-DESSUS n'importe quel
                template (`salleQr: true` dans la slide) et non dans l'un
                d'eux : la slide 3 est une slide standard, et les rappels des
                slides 16 et 29 pourraient un jour vouloir le même bloc sans
                changer de gabarit. */}
            {/* `salleQrInvitation` : la phrase posée au-dessus, et le QR en
                grand — la version d'accueil, sur la slide 1. */}
            {slide.salleQr && <QrSalle invitation={slide.salleQrInvitation || null} />}

            {/* AI generating indicator */}
            {slide.type === "content" && currentPointIndex < totalPointsForTyping && (
                <div className="absolute top-[10%] right-12 flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full animate-pulse z-[70]">
                    <div className="flex gap-1">
                        <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-sm text-cyan-300 font-mono">L'IA génère...</span>
                </div>
            )}

            {/* Navigation */}
            <div className="absolute bottom-8 right-8 flex items-stretch gap-3 z-20 h-11">
                <button
                    onClick={() => onSlideChange(Math.max(0, currentSlide - 1))}
                    disabled={currentSlide === 0}
                    className="px-3 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all hover:scale-105 flex items-center justify-center"
                >
                    <ChevronLeft size={20} />
                </button>

                <div className="px-4 bg-slate-900/60 backdrop-blur-sm border border-blue-500/20 rounded-lg text-sm font-mono flex flex-col items-center justify-center group transition-all duration-300 min-w-[80px]">
                    <span className="leading-none transition-transform duration-300 group-hover:-translate-y-1">
                        {currentSlide + 1} / {slides.length}
                    </span>
                    <div className="absolute bottom-1.5 h-0 group-hover:h-3 transition-all duration-300 overflow-hidden flex items-center justify-center">
                        <input
                            type="checkbox"
                            id="toggleTransitions"
                            checked={transitionsEnabled}
                            onChange={onToggleTransitions}
                            className="w-3 h-3 opacity-0 group-hover:opacity-40 hover:!opacity-100 transition-opacity cursor-pointer accent-blue-500"
                        />
                    </div>
                </div>

                <button
                    onClick={() => onSlideChange(Math.min(slides.length - 1, currentSlide + 1))}
                    disabled={currentSlide === slides.length - 1}
                    className="px-3 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all hover:scale-105 flex items-center justify-center"
                >
                    <ChevronRight size={20} />
                </button>
            </div>

            {/* Progress indicator */}
            <div className="absolute bottom-8 left-8 flex items-center gap-2 z-20">
                {slides.map((_, i) => (
                    <div key={i} className={`h-1 rounded-full transition-all ${i === currentSlide
                        ? 'w-8 bg-cyan-400'
                        : i < currentSlide
                            ? 'w-4 bg-blue-500/50'
                            : 'w-4 bg-blue-500/20'
                        }`} />
                ))}
            </div>
        </div>
    );
};

export default PresentationSlides;
